import { useLayoutEffect, useRef } from 'react'

import Container from '@/components/ui/Container'
import Eyebrow from '@/components/ui/Eyebrow'
import { useData, useLocale } from '@/hooks/useLocale'
import { useScrollDriver } from '@/hooks/useScrollDriver'
import { LOCALES } from '@/lib/locale'
import { clamp, GLIDE, ramp, stickyProgress } from '@/lib/scroll'

const twoDigits = (n) => String(n).padStart(2, '0')

// Each card's accent: its tag pill, and the colour its picture's wash fades to.
const ACCENTS = {
  blue: { tag: 'bg-[#1a73e814] text-[#1a73e8]', wash: 'to-[#1a73e818]' },
  red: { tag: 'bg-[#d9302514] text-[#d93025]', wash: 'to-[#d9302518]' },
  teal: { tag: 'bg-[#0f8b9714] text-[#0f8b97]', wash: 'to-[#0f8b9718]' },
  violet: { tag: 'bg-[#7c4dcc14] text-[#7c4dcc]', wash: 'to-[#7c4dcc18]' },
  orange: { tag: 'bg-[#d9822b14] text-[#d9822b]', wash: 'to-[#d9822b18]' },
  green: { tag: 'bg-[#1f9d5514] text-[#1f9d55]', wash: 'to-[#1f9d5518]' },
}

/**
 * Feature cards (design: Home, Why), each with a coloured tag, a title and a
 * line of text beside a picture of the feature, over a wash of its colour.
 *
 * On screens at least 900px wide and 700px tall (`wide:`) the section pins
 * for a while and scrolling slides the 640px cards sideways, with a counter
 * and bar, until the last card sits flush with the far edge. Focus glides
 * from the first card to the last: the card in focus is full size, the
 * others sit back (92%, faded), and each picture drifts a little against its
 * card. The track is as wide as its cards and slides by the share `--p` of
 * how far it overhangs its container (100cqw), so CSS works out the travel.
 * Like the hero, the cards glide a moment behind the scroll, and each frame
 * goes straight to the DOM, so scrolling never re-renders React.
 *
 * Smaller screens get a carousel to swipe, snapping card by card, with the
 * text above the picture.
 */
export default function WhySection() {
  const { WHY } = useData()
  const { locale } = useLocale()
  const section = useRef(null)
  const track = useRef(null)
  const counter = useRef(null)
  const bar = useRef(null)
  const progress = useRef(0)
  const total = WHY.items.length
  // Pictures drift along the track's direction: rightward in English.
  const side = LOCALES[locale].dir === 'rtl' ? -1 : 1

  // Put everything where the progress has it: the track's share `--p`, each
  // card's focus `--f` and picture drift `--dx`, the counter and the bar.
  const paint = () => {
    // A short rest at each end before the cards start and after they stop.
    const p = ramp(progress.current, 0.04, 0.96)
    const focus = p * (total - 1)
    track.current.style.setProperty('--p', p)
    for (const [i, card] of [...track.current.children].entries()) {
      const away = i - focus
      card.style.setProperty('--f', clamp(1 - Math.abs(away)))
      card.style.setProperty('--dx', `${away * 40 * side}px`)
    }
    counter.current.textContent = `${twoDigits(Math.round(1 + focus))} / ${twoDigits(total)}`
    bar.current.style.width = `${p * 100}%`
  }

  // After every render too: switching language remounts the cards.
  useLayoutEffect(paint)

  useScrollDriver(
    section,
    stickyProgress,
    (value) => {
      progress.current = value
      paint()
    },
    GLIDE,
  )

  return (
    <section
      ref={section}
      id="why"
      className="relative bg-surface py-25 wide:h-[320vh] wide:py-0"
    >
      <div className="wide:sticky wide:top-0 wide:flex wide:h-svh wide:flex-col wide:justify-center wide:overflow-hidden wide:pt-24">
        <Container className="flex w-full flex-col gap-14 wide:gap-10">
          <div className="flex flex-wrap items-end justify-between gap-10">
            <div className="flex min-w-0 flex-1 flex-col gap-5">
              <div className="flex flex-col gap-3.5">
                <Eyebrow className="text-subtle">{WHY.eyebrow}</Eyebrow>
                <h2 className="text-[clamp(32px,3.4vw,46px)] leading-[1.12] tracking-[-0.025em] whitespace-pre-line">
                  {WHY.title}
                </h2>
              </div>
              <p className="text-base/[1.7] text-pretty text-muted">{WHY.text}</p>
            </div>

            <div
              aria-hidden="true"
              className="hidden items-center gap-3 pb-2 wide:flex"
            >
              <span ref={counter} dir="ltr" className="text-[13px] font-semibold" />
              <span className="relative h-0.5 w-40 overflow-hidden rounded-[1px] bg-[#dfe3ea]">
                <span ref={bar} className="absolute inset-y-0 start-0 bg-brand" />
              </span>
            </div>
          </div>

          <div className="@container">
            <div
              ref={track}
              className="-mx-gutter flex snap-x snap-mandatory scroll-px-gutter [scrollbar-width:none] gap-3.5 overflow-x-auto px-gutter pt-2 pb-6 wide:mx-0 wide:w-max wide:translate-x-[calc(var(--p)*(100cqw-100%))] wide:snap-none wide:gap-5 wide:overflow-visible wide:px-0 wide:pt-3 wide:pb-0 wide:will-change-transform rtl:wide:translate-x-[calc(var(--p)*(100%-100cqw))] [&::-webkit-scrollbar]:hidden"
            >
              {WHY.items.map((item) => {
                const accent = ACCENTS[item.accent]
                return (
                  <article
                    key={item.title}
                    className="relative h-[460px] w-[min(640px,calc(100cqw-24px))] shrink-0 snap-start overflow-hidden rounded-[30px] bg-white shadow-[0_1px_2px_rgba(20,30,50,.04),0_24px_48px_-32px_rgba(20,30,50,.3)] wide:h-80 wide:w-160 wide:origin-left wide:scale-[calc(0.92+0.08*var(--f))] wide:opacity-[calc(0.55+0.45*var(--f))] wide:will-change-[transform,opacity] rtl:wide:origin-right"
                  >
                    {/* The wash: up from the bottom; on wide screens in from the far side. */}
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-0 bottom-0 h-3/5 bg-linear-to-b/srgb from-white/0 wide:start-auto wide:top-0 wide:h-auto wide:w-[62%] wide:bg-linear-to-r/srgb rtl:wide:bg-linear-to-l/srgb ${accent.wash}`}
                    />
                    <div className="absolute inset-x-6 top-6 flex flex-col gap-2.5 wide:inset-y-0 wide:start-8 wide:end-auto wide:w-[250px] wide:justify-center wide:gap-3">
                      <span
                        className={`flex h-7 items-center self-start rounded-[14px] px-3 text-xs font-semibold ${accent.tag}`}
                      >
                        {item.tag}
                      </span>
                      <h3 className="text-2xl leading-[1.2] font-semibold tracking-[-0.02em] text-ink">
                        {item.title}
                      </h3>
                      <p className="text-[15px]/[1.6] text-muted">{item.text}</p>
                    </div>
                    <img
                      src={item.image}
                      alt=""
                      width={616}
                      height={432}
                      loading="lazy"
                      decoding="async"
                      className="pointer-events-none absolute inset-x-0 bottom-5 mx-auto w-[min(320px,calc(100%-40px))] drop-shadow-[0_20px_30px_rgba(12,26,51,.12)] wide:start-auto wide:end-6 wide:bottom-7 wide:mx-0 wide:h-56 wide:w-80 wide:translate-x-(--dx) wide:object-contain"
                    />
                  </article>
                )
              })}
            </div>
          </div>
        </Container>
      </div>
    </section>
  )
}
